import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type Claim, patchClaimRequest } from '../../../../models/claim'
import { useDrivers } from '../../../../models/driver'
import { useTrailers, useTrucks } from '../../../../models/equipment'
import { useCurrentUser, useOrganisation } from '../../../../models/user'
import { ClaimDriverSection } from './ClaimDriverSection'
import { ClaimPhotoExtractionBanner } from './ClaimPhotoExtractionBanner'
import {
  claimToStep2Defaults,
  NOT_LISTED,
  STEP2_DEFAULTS,
  STEP2_GROUPS,
  type Step2FormValues,
} from './claimStep2Values'
import { ClaimStepShell } from './ClaimStepShell'
import { ClaimTrailerSection } from './ClaimTrailerSection'
import { ClaimTruckSection } from './ClaimTruckSection'
import { mergeExtractedValues } from './mergeExtractedValues'
import { FILLED_PREFIXES, filledFromPhotos, useClaimPhotoExtraction } from './useClaimPhotoExtraction'

interface ClaimTruckDriverFormProps {
  claimId: string
  /** Org whose fleet populates the dropdowns + scopes the save request. */
  orgId?: string
  claim?: Claim
  onBack: () => void
  onSaved: (claim: Claim) => void
}

export function ClaimTruckDriverForm({ claimId, orgId, claim, onBack, onSaved }: ClaimTruckDriverFormProps) {
  const [saving, setSaving] = useState(false)
  const fleet = { orgId: orgId ?? '', enabled: !!orgId, shouldFetchAllPages: true }
  const { trucks } = useTrucks(fleet)
  const { trailers } = useTrailers(fleet)
  const { drivers } = useDrivers(fleet)

  // Org context for the "showing vehicles on policy X for Y" banner: use the
  // current user's org when it's their own claim, otherwise fetch the on-behalf org.
  const { user } = useCurrentUser({ enabled: true })
  const ownOrg = user?.currentOrg
  const isOwnOrg = !!ownOrg && (!orgId || orgId === ownOrg.id)
  const { organisation } = useOrganisation(user && !isOwnOrg ? orgId : undefined)
  const org = isOwnOrg ? ownOrg : organisation
  const policyIds = (org?.currentPolicies ?? []).map((p) => p.vendorId).filter(Boolean)

  const [initial] = useState(() => (claim ? claimToStep2Defaults(claim) : STEP2_DEFAULTS))
  const { control, register, handleSubmit, formState, reset, getValues, setValue } = useForm<Step2FormValues>(
    {
      defaultValues: initial,
    }
  )
  const errors = formState.errors

  const { extracted, reading, latest } = useClaimPhotoExtraction(claim, orgId)
  useEffect(() => {
    if (!extracted) return
    reset(mergeExtractedValues(claimToStep2Defaults(extracted), getValues(), initial, STEP2_GROUPS))
  }, [extracted, reset, getValues, initial])

  async function onValid(v: Step2FormValues) {
    setSaving(true)
    try {
      // Every box is sent, so one the reporter emptied saves empty rather than refilling from the policy.
      const truck = {
        truckId: v.truckSelection === NOT_LISTED ? null : v.truckSelection,
        licensePlate: v.truckPlate,
        vin: v.truckVin,
        year: v.truckYear.trim() ? Number(v.truckYear.trim()) : null,
        make: v.truckMake,
        model: v.truckModel,
      }

      const attached = v.trailerAttached === true
      const trailer = !attached
        ? null
        : v.trailerSelection === NOT_LISTED
          ? {
              trailerId: null,
              licensePlate: v.trailerPlate,
              vin: v.trailerVin,
              model: v.trailerYearMakeModel,
              trailerOwnership: v.trailerOwnership,
            }
          : { trailerId: v.trailerSelection, licensePlate: v.trailerPlate, vin: v.trailerVin }

      const [first, ...rest] = v.driverName.trim().split(/\s+/)
      const driver =
        v.driverSelection === NOT_LISTED
          ? {
              driverId: null,
              firstName: first,
              lastName: rest.join(' '),
              cdlNumber: v.driverCdl,
              relationshipToInsured: v.relationship,
              wasOnDuty: v.wasOnDuty,
            }
          : {
              driverId: v.driverSelection,
              cdlNumber: v.driverCdl,
              relationshipToInsured: v.relationship,
              wasOnDuty: v.wasOnDuty,
            }

      const saved = await patchClaimRequest(
        claimId,
        { trailerAttached: attached, truck, trailer, driver },
        orgId
      )
      onSaved(saved)
    } catch {
      toast.error('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ClaimStepShell
      step={2}
      title="Confirm Truck & Driver"
      subtitle="We pulled your fleet and drivers from your policy. Pick the truck and driver involved — type a unit number, VIN or name to find them."
    >
      <ClaimPhotoExtractionBanner
        reading={reading}
        filled={filledFromPhotos(latest, FILLED_PREFIXES.equipment)}
      />

      {org && (
        <Banner variant="info" small>
          Showing all active vehicles
          {policyIds.length > 0 &&
            ` on ${policyIds.length === 1 ? 'policy' : 'policies'} ${policyIds.join(', ')}`}{' '}
          for {org.name}.
        </Banner>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Select equipment</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ClaimTruckSection
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            trucks={trucks}
          />
          <ClaimTrailerSection
            control={control}
            register={register}
            setValue={setValue}
            trailers={trailers}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Confirm driver</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ClaimDriverSection
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            drivers={drivers}
          />
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          ← Back
        </Button>
        <Button type="button" onClick={handleSubmit(onValid)} loading={saving ? 'Saving…' : undefined}>
          Next →
        </Button>
      </div>
    </ClaimStepShell>
  )
}
